import { useLocalizedPath } from "../i18n/useLocalizedPath";
import React from "react";
import { Box, Flex, Grid, Heading, Image, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import MaskedReveal from "./MaskedReveal";
import { localizedNewsThumbnail, newsData } from "../data/newsData";
import { imageMap } from "../utils/imageMap";
import { getAssetLanguageKey } from "../i18n/language";
import { getNewsCopy } from "../i18n/content";

const HomeLatestNews: React.FC = () => {
  const { t, i18n } = useTranslation();
  const localizePath = useLocalizedPath();
  const activeLanguage = i18n.resolvedLanguage || i18n.language;
  const assetLang = getAssetLanguageKey(activeLanguage);
  const latestNews = newsData.slice(0, 3);

  const getNewsImage = (item: (typeof newsData)[number]) => {
    const languageImage =
      localizedNewsThumbnail(item, activeLanguage) ||
      item.coverImageByLang?.[assetLang] ||
      item.imagesByLang?.[assetLang]?.[0];
    const image = languageImage || item.thumbnail || item.coverImage || item.images?.[0];
    return image ? imageMap[image] || image : "";
  };

  const getDateDisplay = (item: (typeof newsData)[number]) =>
    getNewsCopy(t, item.id, "dateDisplay") || item.date;

  const getExcerptLines = (html: string) => {
    const plainText = html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<li[^>]*>/gi, "• ")
      .replace(/<\/li>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&amp;/g, "&")
      .replace(/&nbsp;/g, " ")
      .replace(/([^\n])\s*•\s*/g, "$1\n• ")
      .replace(/[ \t]+/g, " ")
      .replace(/ *\n+ */g, "\n")
      .trim();

    return plainText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .slice(0, 2);
  };

  return (
    <Box
      as="section"
      id="latest-news"
      data-testid="home-latest-news"
      overflow="hidden"
      bg="rgba(255,255,255,.76)"
    >
      <Box
        maxW="1400px"
        mx="auto"
        px={{ base: 4, md: 8 }}
        py={{ base: 14, md: 20, lg: 24 }}
      >
        <Grid
          templateColumns={{ base: "1fr", lg: "0.72fr 1.28fr" }}
          gap={{ base: 5, lg: 16 }}
          alignItems="start"
          mb={{ base: 9, md: 14 }}
        >
          <MaskedReveal>
            <Heading
              as="h2"
              color="#25272D"
              fontSize={{ base: "2xl", md: "4xl" }}
              letterSpacing="-0.03em"
            >
              {t("homeStory.latestNews.title")}
            </Heading>
          </MaskedReveal>
          <MaskedReveal delay={0.08}>
            <Text
              maxW="36ch"
              color="gray.600"
              fontSize={{ base: "md", md: "lg" }}
              lineHeight="1.8"
            >
              {t("homeStory.latestNews.statement")}
            </Text>
          </MaskedReveal>
        </Grid>

        <Grid
          templateColumns={{
            base: "repeat(3, minmax(280px, 84vw))",
            md: "repeat(3, minmax(0, 1fr))",
          }}
          gap={{ base: 5, md: 7 }}
          overflowX={{ base: "auto", md: "visible" }}
          pb={{ base: 4, md: 0 }}
          scrollSnapType={{ base: "x mandatory", md: "none" }}
          sx={{
            scrollbarWidth: "thin",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {latestNews.map((item, index) => (
            <MaskedReveal key={item.id} delay={0.08 + index * 0.06}>
              <Box
                as="article"
                scrollSnapAlign={{ base: "start", md: "none" }}
                h="100%"
                display="flex"
                flexDirection="column"
                overflow="hidden"
                borderRadius="2xl"
                border="1px solid"
                borderColor="blackAlpha.200"
                bg="white"
                boxShadow="0 18px 45px rgba(42,55,68,.08)"
              >
                <Box h={{ base: "190px", md: "200px" }} overflow="hidden" bg="gray.100">
                  <Image
                    src={getNewsImage(item)}
                    alt={getNewsCopy(t, item.id, "title")}
                    w="100%"
                    h="100%"
                    objectFit="cover"
                  />
                </Box>
                <Box
                  p={{ base: 5, md: 6 }}
                  display="flex"
                  flexDirection="column"
                  flex="1"
                >
                  <Text
                    color="primary.500"
                    fontSize="xs"
                    fontWeight="700"
                    letterSpacing=".14em"
                    textTransform="uppercase"
                    mb={3}
                  >
                    {t("homeStory.latestNews.mediaLabel")}
                  </Text>
                  <Heading
                    as="h3"
                    fontSize={{ base: "xl", md: "2xl" }}
                    lineHeight="1.35"
                    mb={4}
                    noOfLines={2}
                  >
                    {getNewsCopy(t, item.id, "title")}
                  </Heading>
                  <Box
                    data-testid={`news-excerpt-${item.id}`}
                    color="gray.600"
                    fontSize="sm"
                    lineHeight="1.75"
                    mb={6}
                  >
                    {getExcerptLines(getNewsCopy(t, item.id, "content")).map((line, lineIndex) => (
                      <Text
                        key={`${item.id}-${lineIndex}`}
                        noOfLines={2}
                        mb={lineIndex === 1 ? 0 : 2}
                      >
                        {line}
                      </Text>
                    ))}
                  </Box>
                  <Flex mt="auto" pt={4} justify="space-between" align="center" gap={4}>
                    <Text color="gray.500" fontSize="xs">
                      {getDateDisplay(item)}
                    </Text>
                    <Box
                      as={RouterLink}
                      to={localizePath(`/news/${item.id}`)}
                      color="#25272D"
                      fontSize="sm"
                      fontWeight="700"
                      borderBottom="1px solid"
                      borderColor="currentColor"
                      _hover={{ color: "primary.600" }}
                      _focusVisible={{
                        outline: "3px solid",
                        outlineColor: "primary.400",
                        outlineOffset: "4px",
                      }}
                    >
                      {t("homeStory.latestNews.readMore")}
                    </Box>
                  </Flex>
                </Box>
              </Box>
            </MaskedReveal>
          ))}
        </Grid>

        <MaskedReveal delay={0.18}>
          <Flex justify={{ base: "flex-start", md: "flex-end" }} mt={{ base: 9, md: 12 }}>
            <Box
              as={RouterLink}
              to={localizePath("/news")}
              display="inline-flex"
              alignItems="center"
              gap={2}
              pb={1}
              color="#25272D"
              fontSize={{ base: "md", md: "lg" }}
              fontWeight="700"
              borderBottom="1px solid"
              borderColor="currentColor"
              _hover={{ color: "primary.600", gap: 3 }}
              _focusVisible={{
                outline: "3px solid",
                outlineColor: "primary.400",
                outlineOffset: "5px",
              }}
              transition="gap 180ms ease"
            >
              {t("homeStory.latestNews.allCta")}
              <Box as="span" aria-hidden="true">
                →
              </Box>
            </Box>
          </Flex>
        </MaskedReveal>
      </Box>
    </Box>
  );
};

export default HomeLatestNews;

